import { useParams } from '@tanstack/react-router';
import {
  ArrowRightIcon,
  BotIcon,
  FileTextIcon,
  HashIcon,
  LayersIcon,
  RepeatIcon,
  SparklesIcon,
  TagIcon,
  UserRoundCheckIcon,
  XIcon,
} from 'lucide-react';

import type { WorkflowView } from '@/lib/api/generated/model';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { useLlmProfiles } from '@/features/llm-profiles/hooks/useLlmProfiles';
import { ProfileSummary } from '@/features/llm-profiles/components/ProfileSummary';
import { AiModelsLink } from '@/features/ai-models/components/AiModelsLink';

import { UNASSIGNED_PHASE_ID } from '../builder/types';
import { findStep, resolveTransitionTarget } from '../graph/selection';
import type { GraphSelection } from '../graph/selection';

/** A step's name for display: declared, system, or the raw id as a last resort. */
function stepNameOf(workflow: WorkflowView, stepId: string): string {
  return (
    findStep(workflow, stepId)?.step.name ??
    systemStepName(workflow, stepId) ??
    stepId
  );
}

/**
 * One entry in a list that goes somewhere — a step under a phase, a transition
 * out of a step. Named once because the two lists are the same control, and two
 * copies of a class string this long drift the first time either is touched.
 */
const ENTRY_BUTTON =
  'border-border bg-muted/30 hover:border-primary hover:bg-primary/5 focus-visible:ring-ring flex w-full flex-col gap-0.5 rounded-md border p-2 text-left transition-colors focus-visible:ring-3 focus-visible:outline-none';

/**
 * Text the author wrote, shown whole and in their own whitespace: a step's
 * prompt (FR-043) and a transition's condition. Both are written for the
 * runtime to read, and both lose their meaning when reflowed.
 */
const AUTHORED_TEXT =
  'bg-muted/40 border-border text-foreground max-h-64 overflow-auto rounded-md border p-3 font-mono text-xs whitespace-pre-wrap';

const GATE_MODE_LABEL: Record<string, string> = {
  always_human: 'Always human',
  ai_or_human: 'AI or human',
};

function Field({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof LayersIcon;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <EyebrowLabel icon={Icon}>{label}</EyebrowLabel>
      <span className="text-foreground text-sm">{value}</span>
    </div>
  );
}

/**
 * What an AI step runs on, for anyone reading the diagram (spec 214 US3).
 *
 * It used to print the step's profile reference under "Model" — an internal
 * name, not a model. It now reads the same profile list the builder does and
 * shows the same card, or says plainly that the profile is not there.
 */
function StepProfile({ slug }: { slug: string }) {
  const { entries, isPending, loadFailed } = useLlmProfiles();
  const { workspaceId } = useParams({ strict: false });
  const profile = entries?.find((entry) => entry.slug === slug);
  return (
    <div className="flex flex-col gap-1">
      <EyebrowLabel icon={SparklesIcon}>AI profile</EyebrowLabel>
      {profile ? (
        <>
          <ProfileSummary profile={profile} variant="card" />
          {workspaceId && <AiModelsLink workspaceId={workspaceId} />}
        </>
      ) : isPending ? null : (
        <p className="text-muted-foreground text-sm">
          {loadFailed
            ? "The step's profile could not be loaded."
            : `The profile ${slug} is not available.`}
        </p>
      )}
    </div>
  );
}

/**
 * The read-only graph's detail panel.
 *
 * One shell over two bodies, switching on what is selected — the same shape
 * `BuilderInspector` has over its three. That is the point of it: a transition
 * is a thing you select in the builder, with fields of its own, and here it
 * used to be a list entry inside the step that happened to own it. The same
 * object behaved differently depending on which mode you were reading it in,
 * and `traversal_limit` — which the builder shows and this list did not — was
 * simply invisible to anyone who could not open the builder.
 *
 * Floating rather than docked (FR-041): inspecting something never reflows the
 * chain behind it.
 */
export function GraphDetailPanel({
  workflow,
  selection,
  onSelect,
  onClose,
}: {
  workflow: WorkflowView;
  /** Never `null` here — the page renders no panel when nothing is selected. */
  selection: NonNullable<GraphSelection>;
  /** Step ↔ transition navigation from inside the panel. */
  onSelect: (selection: NonNullable<GraphSelection>) => void;
  onClose: () => void;
}) {
  const title = titleFor(workflow, selection);
  const phone = usePhoneWidth();

  // A bottom sheet on a phone (spec 273): floating over the canvas, the panel
  // covered all of it but a 32px frame and scrolled inside that.
  if (phone) {
    return (
      <Sheet
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle
              className={cn(
                selection.kind === 'transition' && 'font-brand-mono truncate',
              )}
            >
              {title}
            </SheetTitle>
          </SheetHeader>
          <SheetBody className="flex flex-col gap-4 pb-6">
            <Body
              workflow={workflow}
              selection={selection}
              onSelect={onSelect}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      aria-label={`${KIND_LABEL[selection.kind]} details`}
      // 2rem, not 1rem, and the reason is easy to get wrong: an absolutely
      // positioned box is offset from its container's *padding box*, while the
      // canvas it floats over is inset from that same edge by the container's
      // own `p-4`. At `inset-4` the panel therefore lands exactly on the
      // canvas border. `inset-8` is that 1rem of container padding plus a real
      // 1rem gap inside the canvas, on all four sides — which is also why the
      // small-screen width subtracts 4rem rather than 2rem.
      //
      // `shadow-floating` is the one thing the builder's inspector deliberately does
      // not copy. This panel floats above the canvas and the shadow says so;
      // the inspector is a flex sibling, and casting a shadow from something
      // that is not raised would be a lie about depth. Everything else — the
      // width, radius, padding, gap and surface — is shared, so the two read as
      // the same object in two modes.
      className="border-border bg-card absolute inset-y-8 right-8 z-10 flex w-[calc(100%-4rem)] flex-col gap-4 overflow-y-auto rounded-lg border p-4 shadow-floating sm:w-96"
    >
      <div className="flex items-start justify-between gap-2">
        <h2
          className={cn(
            'text-foreground min-w-0 text-base font-medium',
            // A transition's own name is its key, which is an identifier and is
            // set in the face this product uses for identifiers everywhere. A
            // phase and a step both have names their author wrote.
            selection.kind === 'transition' && 'font-brand-mono truncate',
          )}
        >
          {title}
        </h2>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Close ${KIND_LABEL[selection.kind].toLowerCase()} details`}
          onClick={onClose}
        >
          <XIcon />
        </Button>
      </div>

      <Body workflow={workflow} selection={selection} onSelect={onSelect} />
    </aside>
  );
}

/** What each kind of subject is called, in the panel's label and its heading. */
const KIND_LABEL: Record<NonNullable<GraphSelection>['kind'], string> = {
  step: 'Step',
  transition: 'Transition',
  phase: 'Phase',
};

function titleFor(
  workflow: WorkflowView,
  selection: NonNullable<GraphSelection>,
): string {
  switch (selection.kind) {
    case 'step':
      return stepNameOf(workflow, selection.id);
    case 'transition':
      return selection.key;
    case 'phase':
      return phaseNameOf(workflow, selection.id);
  }
}

function Body({
  workflow,
  selection,
  onSelect,
}: {
  workflow: WorkflowView;
  selection: NonNullable<GraphSelection>;
  onSelect: (selection: NonNullable<GraphSelection>) => void;
}) {
  switch (selection.kind) {
    case 'step':
      return (
        <StepBody
          workflow={workflow}
          stepId={selection.id}
          onSelect={onSelect}
        />
      );
    case 'transition':
      return (
        <TransitionBody
          workflow={workflow}
          stepId={selection.stepId}
          transitionKey={selection.key}
          onSelect={onSelect}
        />
      );
    case 'phase':
      return (
        <PhaseBody
          workflow={workflow}
          phaseId={selection.id}
          onSelect={onSelect}
        />
      );
  }
}

/** The name of a system step, or `undefined` if the id names neither. */
function systemStepName(
  workflow: WorkflowView,
  stepId: string,
): string | undefined {
  if (stepId === workflow.done_step.id) return workflow.done_step.name;
  if (stepId === workflow.start_step.id) return workflow.start_step.name;
  return undefined;
}

/**
 * What a step is instructed to do, and what moves the work on from it.
 *
 * The prompt is the reason this body exists, so it is shown whole, with the
 * whitespace the author wrote (FR-043) — the node's preview is deliberately
 * lossy and this is where the loss is repaired. A step with no outgoing
 * transition says so rather than showing an empty region (FR-044): in an
 * accepted workflow that is the last step, and silence there would read as a
 * rendering bug.
 */
function StepBody({
  workflow,
  stepId,
  onSelect,
}: {
  workflow: WorkflowView;
  stepId: string;
  onSelect: (selection: NonNullable<GraphSelection>) => void;
}) {
  const found = findStep(workflow, stepId);

  return (
    <>
      {!found ? (
        <p className="text-muted-foreground text-sm">
          This is a system step: the workflow always starts at{' '}
          {workflow.start_step.name} and finishes at {workflow.done_step.name}.
          It carries no prompt of its own.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-1.5">
              {found.step.type === 'human_gate' ? (
                <UserRoundCheckIcon
                  aria-hidden="true"
                  className="text-muted-foreground size-4"
                  strokeWidth={1.75}
                />
              ) : (
                <BotIcon
                  aria-hidden="true"
                  className="text-muted-foreground size-4"
                  strokeWidth={1.75}
                />
              )}
              <span className="text-foreground text-sm font-medium">
                {found.step.type === 'human_gate' ? 'Human Gate' : 'AI Step'}
              </span>
              {found.step.mode != null && (
                <span className="text-muted-foreground text-sm">
                  · {GATE_MODE_LABEL[found.step.mode] ?? found.step.mode}
                </span>
              )}
            </div>
            <Field label="Phase" value={found.phase.name} icon={LayersIcon} />
            {found.step.llm != null && <StepProfile slug={found.step.llm} />}
          </div>

          <div className="flex shrink-0 flex-col gap-1">
            <EyebrowLabel icon={FileTextIcon}>Prompt</EyebrowLabel>
            <pre data-testid="step-prompt" className={AUTHORED_TEXT}>
              {found.step.prompt ?? ''}
            </pre>
          </div>

          <div className="flex flex-col gap-1.5">
            <EyebrowLabel icon={ArrowRightIcon}>Transitions</EyebrowLabel>
            {(found.step.transitions ?? []).length === 0 ? (
              <p className="text-muted-foreground text-sm">
                This step has no outgoing transition — it is where the chain
                ends.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {(found.step.transitions ?? []).map((transition) => (
                  <li key={transition.key}>
                    {/*
                      A button, because each entry now *goes* somewhere: this is
                      the same step-to-transition navigation the builder's
                      inspector offers, and it is what makes a transition
                      reachable without hitting a 1.5px line on the canvas.
                    */}
                    <button
                      type="button"
                      onClick={() =>
                        onSelect({
                          kind: 'transition',
                          stepId,
                          key: transition.key,
                        })
                      }
                      className={ENTRY_BUTTON}
                    >
                      <span className="text-foreground text-sm">
                        <code className="text-xs">{transition.key}</code> →{' '}
                        {stepNameOf(workflow, transition.to)}
                      </span>
                      {transition.label != null && (
                        <span className="text-muted-foreground text-xs">
                          {transition.label}
                        </span>
                      )}
                      <span className="text-muted-foreground text-xs">
                        {transition.when}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </>
  );
}

/**
 * One transition, in full: where it goes, what names it, and what has to be
 * true for the work to take it.
 *
 * The read-only face of the builder's `TransitionInspector`, field for field —
 * including `traversal_limit`, which the step body's list never showed. A
 * workflow you can only read is exactly where a loop's bound matters, because
 * you cannot open the builder to go and find it.
 *
 * The condition gets the same treatment the step body gives a prompt: shown
 * whole, in the author's own whitespace. It is prose written for the runtime to
 * evaluate, and the canvas has about eight characters of room for it — this is
 * the only place it is ever readable.
 */
function TransitionBody({
  workflow,
  stepId,
  transitionKey,
  onSelect,
}: {
  workflow: WorkflowView;
  stepId: string;
  transitionKey: string;
  onSelect: (selection: NonNullable<GraphSelection>) => void;
}) {
  const owner = findStep(workflow, stepId);
  const transition = owner?.step.transitions?.find(
    (candidate) => candidate.key === transitionKey,
  );

  if (!transition) {
    // Reachable from a link that outlived a rename. It says so rather than
    // rendering an empty panel, which would read as a failure to load.
    return (
      <p className="text-muted-foreground text-sm">
        This transition is no longer part of the workflow. It may have been
        renamed or removed since the link was shared.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <EyebrowLabel icon={ArrowRightIcon}>Goes</EyebrowLabel>
          <span className="text-foreground text-sm">
            {/*
              The source is a button and the target is not. Stepping back to the
              step that owns this transition is the move a reader actually makes
              — it is where the prompt is — while the target is reachable by
              clicking the node the arrow points at.
            */}
            <button
              type="button"
              onClick={() => onSelect({ kind: 'step', id: stepId })}
              className="hover:text-foreground focus-visible:ring-ring rounded underline decoration-dotted underline-offset-2 focus-visible:ring-3 focus-visible:outline-none"
            >
              {stepNameOf(workflow, stepId)}
            </button>{' '}
            →{' '}
            {stepNameOf(
              workflow,
              resolveTransitionTarget(workflow, transition.to),
            )}
          </span>
        </div>
        {transition.label != null && transition.label !== '' && (
          <Field label="Label" value={transition.label} icon={TagIcon} />
        )}
        {transition.traversal_limit != null && (
          <Field
            label="Traversal limit"
            value={`${String(transition.traversal_limit)} ${
              transition.traversal_limit === 1 ? 'time' : 'times'
            }`}
            icon={RepeatIcon}
          />
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-1">
        <EyebrowLabel icon={FileTextIcon}>Condition</EyebrowLabel>
        <pre data-testid="transition-condition" className={AUTHORED_TEXT}>
          {transition.when}
        </pre>
      </div>
    </>
  );
}

/**
 * A phase: what it is called, what it is called in the document, and what is
 * in it.
 *
 * The read-only face of the builder's `PhaseInspector`. A band used to be
 * background on this canvas — clicking one cleared the selection — which left
 * the one part of a workflow that groups the rest unable to say anything about
 * itself. Its steps are listed because that is what a phase *is*: a name and
 * the work under it.
 */
function PhaseBody({
  workflow,
  phaseId,
  onSelect,
}: {
  workflow: WorkflowView;
  phaseId: string;
  onSelect: (selection: NonNullable<GraphSelection>) => void;
}) {
  const phase = workflow.phases.find((candidate) => candidate.id === phaseId);
  const steps = (phase?.steps ?? []).filter(
    (step) => step.type !== 'backlog' && step.type !== 'done',
  );

  if (!phase) {
    return (
      <p className="text-muted-foreground text-sm">
        This phase is no longer part of the workflow. It may have been renamed
        or removed since the link was shared.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <Field label="Identifier" value={phase.id} icon={HashIcon} />
        {phase.id === UNASSIGNED_PHASE_ID && (
          // Not a stage of the work: it is where steps wait until an author
          // files them, and a reader should not mistake it for part of the
          // process being described.
          <p className="text-muted-foreground text-sm">
            A holding area rather than a stage — steps here have not been filed
            under a phase yet.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <EyebrowLabel icon={LayersIcon}>
          {steps.length === 1 ? '1 step' : `${String(steps.length)} steps`}
        </EyebrowLabel>
        {steps.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing is in this phase yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {steps.map((step) => (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => onSelect({ kind: 'step', id: step.id })}
                  className={ENTRY_BUTTON}
                >
                  <span className="text-foreground text-sm">{step.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {step.type === 'human_gate' ? 'Human Gate' : 'AI Step'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/** A phase's name, with the reserved holding phase spelled out. */
function phaseNameOf(workflow: WorkflowView, phaseId: string): string {
  if (phaseId === UNASSIGNED_PHASE_ID) return 'Unassigned';
  return workflow.phases.find((phase) => phase.id === phaseId)?.name ?? phaseId;
}
